import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users,
  Search,
  Edit,
  Plus,
  Loader2,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
  X,
  CheckCircle,
  User,
  Calendar,
  Clock,
  Eye,
  Mail,
  Link2,
  RefreshCw,
  Ban,
  Copy,
  ClipboardList,
  Sparkles,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetClose } from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import PatientFormDrawer from '@/components/clinic/PatientFormDrawer';
import UpdatePatientStatusDialog from '@/components/patient/UpdatePatientStatusDialog';
import ConfirmDeleteDialog from '@/components/ui/confirm-delete-dialog';
import SendPatientFormLinkDialog from '@/components/patient/SendPatientFormLinkDialog';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getDefaultContactValue } from '@/lib/utils';
import { PaginationParams, Patient } from '@/lib/types';
import { patientService } from '@/services/patientService';
import { practitionerService } from '@/services/practitionerService';
import { formatToLocalDate, toTitleCase } from '@/lib/utils';
import { formatDOB } from '@/lib/dateUtils';
import { useClinic } from '@/contexts/ClinicContext';
import SmartPagination from '../ui/smart-pagination';
import { DatePicker } from '../ui/date-picker';
import { permissionCheck } from '@/lib/permission-check';

// Filter options
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'unknown', label: 'Unknown' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const ClinicPatients = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, isPractitioner, rolePermissions, clinic, isGuided } = useClinic();
  const [activeTab, setActiveTab] = useState('patients');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  const [sendFormLinkDialogOpen, setSendFormLinkDialogOpen] = useState(false);
  const [patientForFormLink, setPatientForFormLink] = useState<Patient | null>(null);
  const [sendingFormLink, setSendingFormLink] = useState(false);

  // Patient Form Links states
  const [formLinks, setFormLinks] = useState<any[]>([]);
  const [formLinksLoading, setFormLinksLoading] = useState(false);
  const [formLinksPagination, setFormLinksPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [linkToRevoke, setLinkToRevoke] = useState<any>(null);
  const [revokingLink, setRevokingLink] = useState(false);
  const [sendNewLinkDialogOpen, setSendNewLinkDialogOpen] = useState(false);

  // Status update dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [patientForStatusUpdate, setPatientForStatusUpdate] = useState<Patient | null>(null);
  const [pendingStatus, setPendingStatus] = useState<'active' | 'inactive'>('active');

  // Filter states
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [tempGender, setTempGender] = useState<string>('all');
  const [tempStatus, setTempStatus] = useState<string>('all');
  const [tempPractitioner, setTempPractitioner] = useState<string>('all');
  const [tempSortBy, setTempSortBy] = useState<string>('created_at');
  const [tempSortOrder, setTempSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [tempDobFrom, setTempDobFrom] = useState<Date | undefined>(undefined);
  const [tempDobTo, setTempDobTo] = useState<Date | undefined>(undefined);

  // Applied filter states
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [practitionerFilter, setPractitionerFilter] = useState<string>('all');
  const [dobFromFilter, setDobFromFilter] = useState<Date | undefined>(undefined);
  const [dobToFilter, setDobToFilter] = useState<Date | undefined>(undefined);

  const [practitioners, setPractitioners] = useState<{ value: string; label: string }[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    totalItems: 0,
    itemsPerPage: 10,
    totalPages: 0,
    sortBy: 'created_at',
    sortOrder: 'DESC',
    filter: {
      clinics: [clinic?.id],
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge
            className='inline-flex w-fit items-center gap-1 border-0 bg-emerald-600 px-2 py-0.5 font-medium text-white hover:bg-emerald-600'
            style={{ fontSize: '0.7rem' }}
          >
            <CheckCircle className='h-3 w-3' />
            Active
          </Badge>
        );
      case 'inactive':
        return (
          <Badge
            className='inline-flex w-fit items-center gap-1 border-0 bg-slate-500 px-2 py-0.5 font-medium text-white hover:bg-slate-500'
            style={{ fontSize: '0.7rem' }}
          >
            Inactive
          </Badge>
        );
      default:
        return (
          <Badge
            className='inline-flex w-fit items-center border-0 bg-slate-500 px-2 py-0.5 font-medium text-white hover:bg-slate-500'
            style={{ fontSize: '0.7rem' }}
          >
            {toTitleCase(status || '-')}
          </Badge>
        );
    }
  };

  const handleSort = (field: string) => {
    if (field === pagination.sortBy) {
      setPagination(prev => ({
        ...prev,
        page: 1,
        sortOrder: pagination.sortOrder === 'ASC' ? 'DESC' : 'ASC',
      }));
    } else {
      setPagination(prev => ({
        ...prev,
        page: 1,
        sortBy: field,
        sortOrder: 'ASC',
      }));
    }
  };

  const currentPage = pagination.page || 1;
  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleStatusChangeClick = (patient: Patient, newStatus: 'active' | 'inactive') => {
    setPatientForStatusUpdate(patient);
    setPendingStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleStatusUpdateSuccess = async () => {
    await fetchPatients();
  };

  const handleAdd = () => {
    setEditingPatient(null);
    setIsDrawerOpen(true);
  };

  const handleSendNewLink = () => {
    setSendNewLinkDialogOpen(true);
  };

  const handleEdit = (patient: Patient) => {
    setEditingPatient(patient);
    setIsDrawerOpen(true);
  };

  const handleSendFormLink = (patient: Patient) => {
    setPatientForFormLink(patient);
    setSendFormLinkDialogOpen(true);
  };

  const handleSendFormLinkConfirm = async () => {
    if (!patientForFormLink) return;

    // Find patient email (respect is_default in contacts)
    const patientEmail = getDefaultContactValue(patientForFormLink.contacts, 'email');

    if (!patientEmail) {
      toast({
        title: 'Error',
        description: 'Patient does not have an email address on file.',
        variant: 'destructive',
      });
      return;
    }

    setSendingFormLink(true);
    try {
      const response = await patientService.generatePatientFormLink({
        email: patientEmail,
        patient_id: patientForFormLink.id,
        clinic_id: clinic?.id || '',
        practitioner_id: patientForFormLink.practitioner_id || user?.practitionerId || '',
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: `Form link sent to ${patientEmail}`,
          variant: 'success',
        });
        setSendFormLinkDialogOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to send form link',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to send form link',
        variant: 'destructive',
      });
    } finally {
      setSendingFormLink(false);
    }
  };

  const handleCardClick = (patientId: number) => {
    if (isAdmin) {
      navigate(`/patients/${patientId}`);
    } else if (isPractitioner) {
      navigate(`/practitioner-portal/patients/${patientId}`);
    } else {
      navigate(`/patients/${patientId}`);
    }
  };

  const handleDeleteClick = (patient: Patient) => {
    setPatientToDelete(patient);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (patientToDelete) {
      setPatients(prev => prev.filter(p => p.id !== patientToDelete.id));
      toast({
        variant: 'success',
        title: 'Success',
        description: 'Patient deleted successfully',
      });
      setPatientToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // Fetch patient form links
  const fetchFormLinks = async () => {
    setFormLinksLoading(true);
    try {
      const response = await patientService.getPatientFormLinks(clinic?.id || '', {
        page: formLinksPagination.page,
        limit: formLinksPagination.limit,
        search: searchTerm,
      });

      if (response.success) {
        setFormLinks(response.data.links);
        setFormLinksPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching form links:', error);
      toast({
        title: 'Error',
        description: 'Failed to load form links',
        variant: 'destructive',
      });
    } finally {
      setFormLinksLoading(false);
    }
  };

  // Handle revoke form link
  const handleRevokeLink = (link: any) => {
    setLinkToRevoke(link);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!linkToRevoke) return;

    setRevokingLink(true);
    try {
      const response = await patientService.revokePatientFormLink(
        clinic?.id || '',
        linkToRevoke.id
      );

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Form link revoked successfully',
          variant: 'success',
        });
        fetchFormLinks();
        setRevokeDialogOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to revoke form link',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to revoke form link',
        variant: 'destructive',
      });
    } finally {
      setRevokingLink(false);
    }
  };

  // Handle resend form link
  const handleResendLink = async (link: any) => {
    try {
      const response = await patientService.resendPatientFormLink(clinic?.id || '', link.id);

      if (response.success) {
        toast({
          title: 'Success',
          description: `Form link resent to ${link.email}`,
          variant: 'success',
        });
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to resend form link',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to resend form link',
        variant: 'destructive',
      });
    }
  };

  // Copy link to clipboard
  const handleCopyLink = (token: string) => {
    const link = `${window.location.origin}/patient-form/${token}`;
    navigator.clipboard.writeText(link);
    toast({
      title: 'Success',
      description: 'Form link copied to clipboard',
      variant: 'success',
    });
  };

  // Rotate colors for form link icons
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
    { bg: 'bg-teal-100', text: 'text-teal-600' },
  ];

  // Get status badge for form links
  const getFormLinkStatusBadge = (status: string, expiresAt: string) => {
    const isExpired = new Date(expiresAt) < new Date();

    if (status === 'used') {
      return (
        <Badge className='inline-flex w-fit items-center gap-1 border-0 bg-emerald-600 text-xs text-white hover:bg-emerald-600'>
          <CheckCircle className='h-3 w-3' />
          <span>Used</span>
        </Badge>
      );
    } else if (status === 'revoked') {
      return (
        <Badge className='inline-flex w-fit items-center gap-1 border-0 bg-slate-500 text-xs text-white hover:bg-slate-500'>
          <Ban className='h-3 w-3' />
          <span>Revoked</span>
        </Badge>
      );
    } else if (isExpired) {
      return (
        <Badge className='inline-flex w-fit items-center gap-1 border-0 bg-red-600 text-xs text-white hover:bg-red-600'>
          <Clock className='h-3 w-3' />
          <span>Expired</span>
        </Badge>
      );
    } else {
      return (
        <Badge className='inline-flex w-fit items-center gap-1 border-0 bg-amber-500 text-xs text-white hover:bg-amber-500'>
          <Clock className='h-3 w-3' />
          <span>Active</span>
        </Badge>
      );
    }
  };

  const handlePatientSubmit = (data: any) => {
    fetchPatients();
    setIsDrawerOpen(false);
    setEditingPatient(null);
  };

  // Helper to format date to YYYY-MM-DD without timezone shift
  const formatDateToYMD = (date: Date | undefined) => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchPatients = async () => {
    setIsLoading(true);
    try {
      const filter: any = {
        clinics: [clinic?.id],
      };
      if (genderFilter !== 'all') filter.genders = [genderFilter];
      if (statusFilter !== 'all') filter.statuses = [statusFilter];
      if (practitionerFilter !== 'all') filter.practitioners = [practitionerFilter];
      if (dobFromFilter) filter.dobFrom = formatDateToYMD(dobFromFilter);
      if (dobToFilter) filter.dobTo = formatDateToYMD(dobToFilter);

      const response = await patientService.getPatient({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        sortBy: pagination.sortBy,
        sortOrder: (pagination.sortOrder?.toUpperCase() as 'ASC' | 'DESC') || 'DESC',
        filter,
      });
      setPatients(response.data.patients);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to fetch patients',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch practitioners for filter dropdown
  useEffect(() => {
    const fetchPractitioners = async () => {
      if (!clinic?.id) return;
      try {
        const response = await practitionerService.getPractitionerDropdownByClinic(clinic.id, {});
        const practitionerOptions = (response.data.practitioners || []).map((p: any) => ({
          value: p.id,
          label: p.full_name,
        }));
        setPractitioners(practitionerOptions);
      } catch (error) {
        console.error('Failed to fetch practitioners:', error);
      }
    };
    fetchPractitioners();
  }, [clinic?.id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPagination(prev => ({
        ...prev,
        page: 1,
      }));
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Load form links when switching to form links tab
  useEffect(() => {
    if (activeTab === 'form-links') {
      fetchFormLinks();
    }
  }, [activeTab, formLinksPagination.page]);

  useEffect(() => {
    fetchPatients();
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    pagination.sortBy,
    pagination.sortOrder,
    genderFilter,
    statusFilter,
    practitionerFilter,
    dobFromFilter,
    dobToFilter,
  ]);

  const SortIcon = ({ field }: { field: string }) => {
    if (pagination.sortBy !== field) return <ChevronUp className='h-4 w-4 text-slate-300' />;
    return pagination.sortOrder === 'ASC' ? (
      <ChevronUp className='h-4 w-4 text-slate-700' />
    ) : (
      <ChevronDown className='h-4 w-4 text-slate-700' />
    );
  };

  const hasActiveFilters =
    genderFilter !== 'all' ||
    statusFilter !== 'all' ||
    practitionerFilter !== 'all' ||
    dobFromFilter ||
    dobToFilter ||
    pagination.sortBy !== 'created_at' ||
    pagination.sortOrder !== 'DESC';

  const getActiveFilterCount = () => {
    let count = 0;
    if (genderFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    if (!isGuided && practitionerFilter !== 'all') count++;
    if (dobFromFilter || dobToFilter) count++;
    if (pagination.sortBy !== 'created_at' || pagination.sortOrder !== 'DESC') count++;
    return count;
  };

  const hasCreatePermission = permissionCheck(
    'patients:create',
    rolePermissions,
    isPractitioner || isAdmin
  );

  const hasEditPermission = permissionCheck(
    'patients:update',
    rolePermissions,
    isPractitioner || isAdmin
  );

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
            Patients
          </h1>
          <p className='mt-1 text-sm text-slate-500'>Manage and view all registered patients</p>
        </div>
        {hasCreatePermission && (
          <div className='flex gap-2'>
            <Button
              onClick={handleSendNewLink}
              variant='outline'
              className='rounded-lg border-brand-teal font-medium text-brand-teal shadow-sm hover:bg-brand-teal/10'
            >
              <Mail className='h-4 w-4' />
              Send Link
            </Button>
            <Button
              onClick={handleAdd}
              className='rounded-lg bg-brand-teal font-medium text-white shadow-sm hover:bg-brand-teal/90'
            >
              <Plus className='h-4 w-4' />
              Add Patient
            </Button>
          </div>
        )}
      </div>

      {/* Filters Row */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
        <TabsList
          className='h-auto w-full justify-start gap-1 rounded-lg border border-slate-200 p-1 sm:w-auto'
          style={{ backgroundColor: 'rgb(245, 247, 247)' }}
        >
          <TabsTrigger
            value='patients'
            className='flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm'
          >
            <Users className='h-4 w-4' />
            Patients
          </TabsTrigger>
          <TabsTrigger
            value='form-links'
            className='flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm'
          >
            <Link2 className='h-4 w-4' />
            Form Links
          </TabsTrigger>
        </TabsList>

        {/* Patients Tab */}
        <TabsContent value='patients' className='mt-4 space-y-4'>
          <div className='flex flex-row items-center gap-3'>
            {/* Search */}
            <div className='relative max-w-md flex-1'>
              <Search className='absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400' />
              <Input
                placeholder='Search by name, email, phone, patient ID, or DOB'
                className='rounded-lg border-slate-200 bg-white pl-9'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value.trimStart())}
              />
            </div>

            {/* Filter Options */}
            <Button
              variant='outline'
              size='sm'
              className='ml-auto h-9 gap-2 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-700'
              onClick={() => {
                setTempGender(genderFilter);
                setTempStatus(statusFilter);
                setTempPractitioner(practitionerFilter);
                setTempSortBy(pagination.sortBy || 'created_at');
                setTempSortOrder(pagination.sortOrder || 'DESC');
                setTempDobFrom(dobFromFilter);
                setTempDobTo(dobToFilter);
                setIsFilterDrawerOpen(true);
              }}
            >
              <SlidersHorizontal className='h-4 w-4' />
              Filter
              {getActiveFilterCount() > 0 && (
                <Badge className='ml-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-0 bg-red-500 px-1.5 py-0.5 text-xs text-white'>
                  {getActiveFilterCount()}
                </Badge>
              )}
            </Button>
          </div>

          {/* Active Filters Pills */}
          {hasActiveFilters && (
            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-xs text-slate-500'>Filters:</span>
              {genderFilter !== 'all' && (
                <Badge
                  variant='secondary'
                  className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
                >
                  Gender: {GENDER_OPTIONS.find(o => o.value === genderFilter)?.label}
                  <button
                    onClick={() => {
                      setGenderFilter('all');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className='ml-1.5 hover:text-slate-900'
                  >
                    <X className='h-3 w-3' />
                  </button>
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge
                  variant='secondary'
                  className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
                >
                  Status: {STATUS_OPTIONS.find(o => o.value === statusFilter)?.label}
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className='ml-1.5 hover:text-slate-900'
                  >
                    <X className='h-3 w-3' />
                  </button>
                </Badge>
              )}
              {!isGuided && practitionerFilter !== 'all' && (
                <Badge
                  variant='secondary'
                  className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
                >
                  Practitioner: {practitioners.find(p => p.value === practitionerFilter)?.label}
                  <button
                    onClick={() => {
                      setPractitionerFilter('all');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className='ml-1.5 hover:text-slate-900'
                  >
                    <X className='h-3 w-3' />
                  </button>
                </Badge>
              )}
              {(dobFromFilter || dobToFilter) && (
                <Badge
                  variant='secondary'
                  className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
                >
                  DOB: {dobFromFilter ? formatDateToYMD(dobFromFilter) : 'Any'} -{' '}
                  {dobToFilter ? formatDateToYMD(dobToFilter) : 'Any'}
                  <button
                    onClick={() => {
                      setDobFromFilter(undefined);
                      setDobToFilter(undefined);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className='ml-1.5 hover:text-slate-900'
                  >
                    <X className='h-3 w-3' />
                  </button>
                </Badge>
              )}
              {pagination.sortBy !== 'created_at' && (
                <Badge
                  variant='secondary'
                  className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
                >
                  Sort: {pagination.sortBy === 'name' ? 'Name' : pagination.sortBy}
                  <button
                    onClick={() => {
                      setPagination(prev => ({ ...prev, page: 1, sortBy: 'created_at' }));
                    }}
                    className='ml-1.5 hover:text-slate-900'
                  >
                    <X className='h-3 w-3' />
                  </button>
                </Badge>
              )}
              {pagination.sortOrder !== 'DESC' && (
                <Badge
                  variant='secondary'
                  className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
                >
                  Order: Ascending
                  <button
                    onClick={() => {
                      setPagination(prev => ({ ...prev, page: 1, sortOrder: 'DESC' }));
                    }}
                    className='ml-1.5 hover:text-slate-900'
                  >
                    <X className='h-3 w-3' />
                  </button>
                </Badge>
              )}
              <button
                onClick={() => {
                  setGenderFilter('all');
                  setStatusFilter('all');
                  setPractitionerFilter('all');
                  setDobFromFilter(undefined);
                  setDobToFilter(undefined);
                  setPagination(prev => ({
                    ...prev,
                    page: 1,
                    sortBy: 'created_at',
                    sortOrder: 'DESC',
                  }));
                }}
                className='text-xs text-brand-teal hover:underline'
              >
                Clear all
              </button>
            </div>
          )}

          {/* Table */}
          <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
            <div className='overflow-x-auto'>
              <table className='w-full min-w-[1000px] text-sm'>
                <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                  <tr className='border-b border-slate-200'>
                    <th
                      className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      #
                    </th>
                    <th
                      className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                      style={{ fontSize: '0.7rem' }}
                      onClick={() => handleSort('name')}
                    >
                      <div className='flex items-center gap-1'>
                        Patient Name
                        <SortIcon field='name' />
                      </div>
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Gender
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      DOB
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Email
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Phone
                    </th>
                    {/* <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                  Species
                </th> */}
                    {!isGuided && (
                      <th
                        className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                        style={{ fontSize: '0.7rem' }}
                      >
                        Practitioner
                      </th>
                    )}
                    <th
                      className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                      style={{ fontSize: '0.7rem' }}
                      onClick={() => handleSort('status')}
                    >
                      <div className='flex items-center gap-1'>
                        Status
                        <SortIcon field='status' />
                      </div>
                    </th>
                    <th
                      className='sticky right-0 w-12 px-2 py-3'
                      style={{
                        background:
                          'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                      }}
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={10} className='h-48 text-center'>
                        <div className='flex flex-col items-center justify-center text-slate-500'>
                          <Loader2 className='mb-3 h-8 w-8 animate-spin text-brand-teal' />
                          <p className='text-sm font-medium'>Loading patients...</p>
                        </div>
                      </td>
                    </tr>
                  ) : patients.length === 0 ? (
                    <tr>
                      <td colSpan={10} className='h-48 text-center'>
                        <div className='flex flex-col items-center justify-center text-slate-500'>
                          <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                            <Users className='h-7 w-7 text-slate-400' />
                          </div>
                          <p className='font-medium text-slate-600'>
                            {searchTerm ? 'No patients found' : 'No patients yet'}
                          </p>
                          <p className='mt-1 text-sm text-slate-400'>
                            {searchTerm
                              ? 'Try adjusting your search'
                              : 'Add your first patient to get started'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    patients.map((patient, index) => {
                      // Rotate colors for user icons (10 different colors)
                      const colors = [
                        { bg: 'bg-blue-100', text: 'text-blue-600' },
                        { bg: 'bg-purple-100', text: 'text-purple-600' },
                        { bg: 'bg-pink-100', text: 'text-pink-600' },
                        { bg: 'bg-orange-100', text: 'text-orange-600' },
                        { bg: 'bg-amber-100', text: 'text-amber-600' },
                        { bg: 'bg-cyan-100', text: 'text-cyan-600' },
                        { bg: 'bg-indigo-100', text: 'text-indigo-600' },
                        { bg: 'bg-rose-100', text: 'text-rose-600' },
                        { bg: 'bg-violet-100', text: 'text-violet-600' },
                        { bg: 'bg-sky-100', text: 'text-sky-600' },
                      ];
                      const colorIndex = index % colors.length;
                      const iconColor = colors[colorIndex];

                      return (
                        <tr
                          key={patient.id}
                          className='cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/80'
                          onClick={() => handleCardClick(patient.id)}
                        >
                          <td className='px-4 py-3 text-slate-500' style={{ fontSize: '0.8rem' }}>
                            #
                            {String(
                              (currentPage - 1) * (pagination.limit || 10) + index + 1
                            ).padStart(3, '0')}
                          </td>
                          <td className='max-w-[200px] px-4 py-3' style={{ fontSize: '0.8rem' }}>
                            <div className='flex items-center gap-3'>
                              <div
                                className={`h-8 w-8 ${iconColor.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
                              >
                                <User className={`h-4 w-4 ${iconColor.text}`} />
                              </div>
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className='block max-w-[150px] cursor-default truncate font-medium capitalize text-slate-900'>
                                      {patient.full_name}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side='top'
                                    className='max-w-[300px] border-black bg-black text-xs text-white [&>svg]:fill-black'
                                  >
                                    {patient.full_name}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </td>
                          <td className='px-4 py-3 text-slate-900' style={{ fontSize: '0.8rem' }}>
                            {toTitleCase(patient?.gender || '--')}
                          </td>
                          <td className='px-4 py-3 text-slate-900' style={{ fontSize: '0.8rem' }}>
                            {patient?.dob
                              ? formatDOB(patient.dob)
                              : '--'}
                          </td>
                          <td
                            className='max-w-[180px] px-4 py-3 text-slate-900'
                            style={{ fontSize: '0.8rem' }}
                          >
                            <span
                              className='block truncate'
                              title={
                                getDefaultContactValue(patient?.contacts, 'email') || '-'
                              }
                            >
                              {getDefaultContactValue(patient?.contacts, 'email') || '--'}
                            </span>
                          </td>
                          <td className='px-4 py-3 text-slate-900' style={{ fontSize: '0.8rem' }}>
                            {getDefaultContactValue(patient?.contacts, 'phone') || '--'}
                          </td>
                          {/* <td className="px-4 py-3 text-slate-900" style={{ fontSize: '0.8rem' }}>
                      {toTitleCase(patient?.species || '--')}
                    </td> */}
                          {!isGuided && (
                            <td
                              className='max-w-[150px] px-4 py-3 text-slate-900'
                              style={{ fontSize: '0.8rem' }}
                            >
                              <span
                                className='block truncate'
                                title={patient?.practitioner?.full_name || '-'}
                              >
                                {patient?.practitioner?.full_name || '--'}
                              </span>
                            </td>
                          )}
                          <td className='px-4 py-3'>{getStatusBadge(patient.status)}</td>
                          <td
                            className='sticky right-0 w-12 px-2 py-3'
                            style={{
                              background:
                                'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            {hasEditPermission && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant='ghost'
                                    size='icon'
                                    className='h-8 w-8 hover:bg-slate-100'
                                  >
                                    <MoreHorizontal className='h-4 w-4 text-slate-500' />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align='end' className='w-48'>
                                  <DropdownMenuItem
                                    onClick={() => handleCardClick(patient.id)}
                                    className='cursor-pointer'
                                    style={{ fontSize: '13px' }}
                                  >
                                    <Eye className='mr-2 h-4 w-4 text-slate-500' />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleEdit(patient)}
                                    className='cursor-pointer'
                                    style={{ fontSize: '13px' }}
                                  >
                                    <Edit className='mr-2 h-4 w-4 text-slate-500' />
                                    Edit
                                  </DropdownMenuItem>
                                  {patient.status === 'active' && hasEditPermission && (
                                    <DropdownMenuItem
                                      onClick={() => handleStatusChangeClick(patient, 'inactive')}
                                      className='cursor-pointer'
                                      style={{ fontSize: '13px' }}
                                    >
                                      <Ban className='mr-2 h-4 w-4 text-slate-500' />
                                      Deactivate
                                    </DropdownMenuItem>
                                  )}
                                  {patient.status === 'inactive' && hasEditPermission && (
                                    <DropdownMenuItem
                                      onClick={() => handleStatusChangeClick(patient, 'active')}
                                      className='cursor-pointer'
                                      style={{ fontSize: '13px' }}
                                    >
                                      <CheckCircle className='mr-2 h-4 w-4 text-emerald-600' />
                                      Activate
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className='flex flex-col items-center gap-3 border-t border-slate-200 px-2 py-3 sm:flex-row sm:justify-between sm:px-4'>
                <div className='w-full overflow-x-auto sm:w-auto'>
                  <SmartPagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    className='mx-0 justify-center sm:justify-start'
                    maxVisiblePages={3}
                  />
                </div>
                <p className='whitespace-nowrap text-xs text-slate-500'>
                  Total: <span className='font-medium text-slate-700'>{pagination.totalItems}</span>{' '}
                  Patients
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Form Links Tab */}
        <TabsContent value='form-links' className='mt-4'>
          <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
            {/* Table */}
            <div className='overflow-x-auto'>
              <table className='w-full min-w-[900px] text-sm'>
                <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                  <tr className='border-b border-slate-200'>
                    <th
                      className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      #
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Email
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Patient
                    </th>
                    {!isGuided && (
                      <th
                        className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                        style={{ fontSize: '0.7rem' }}
                      >
                        Practitioner
                      </th>
                    )}
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Created
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Expires
                    </th>
                    <th
                      className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                      style={{ fontSize: '0.7rem' }}
                    >
                      Status
                    </th>
                    <th
                      className='sticky right-0 w-12 px-2 py-3'
                      style={{
                        background:
                          'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                      }}
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  {formLinksLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index} className='border-b border-slate-100'>
                        <td className='px-4 py-3'>
                          <div className='h-4 w-10 animate-pulse rounded bg-slate-200' />
                        </td>
                        <td className='px-4 py-3'>
                          <div className='flex items-center gap-3'>
                            <div className='h-8 w-8 animate-pulse rounded-lg bg-slate-200' />
                            <div className='h-4 w-40 animate-pulse rounded bg-slate-200' />
                          </div>
                        </td>
                        <td className='px-4 py-3'>
                          <div className='h-4 w-28 animate-pulse rounded bg-slate-200' />
                        </td>
                        <td className='px-4 py-3'>
                          <div className='h-4 w-28 animate-pulse rounded bg-slate-200' />
                        </td>
                        <td className='px-4 py-3'>
                          <div className='h-4 w-24 animate-pulse rounded bg-slate-200' />
                        </td>
                        <td className='px-4 py-3'>
                          <div className='h-4 w-24 animate-pulse rounded bg-slate-200' />
                        </td>
                        <td className='px-4 py-3'>
                          <div className='h-5 w-16 animate-pulse rounded-full bg-slate-200' />
                        </td>
                        <td className='px-4 py-3'>
                          <div className='h-8 w-8 animate-pulse rounded bg-slate-200' />
                        </td>
                      </tr>
                    ))
                  ) : formLinks.length === 0 ? (
                    <tr>
                      <td colSpan={8} className='h-48 text-center'>
                        <div className='flex flex-col items-center justify-center text-slate-500'>
                          <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                            <Link2 className='h-7 w-7 text-slate-400' />
                          </div>
                          <p className='font-medium text-slate-600'>No form links found</p>
                          <p className='mt-1 text-sm text-slate-400'>
                            Create form links to invite patients
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    formLinks.map((link, index) => {
                      const color = colors[index % colors.length];
                      const isExpired = new Date(link.expires_at) < new Date();
                      const canResend = link.status === 'active' && !isExpired;
                      const canRevoke = link.status === 'active';

                      return (
                        <tr
                          key={link.id}
                          className='border-b border-slate-100 transition-colors hover:bg-slate-50/50'
                        >
                          <td
                            className='whitespace-nowrap px-4 py-3 text-slate-500'
                            style={{ fontSize: '0.8rem' }}
                          >
                            #
                            {String(
                              (formLinksPagination.page - 1) * formLinksPagination.limit + index + 1
                            ).padStart(3, '0')}
                          </td>
                          <td
                            className='max-w-[250px] whitespace-nowrap px-4 py-3'
                            style={{ fontSize: '0.8rem' }}
                          >
                            <div className='flex items-center gap-3'>
                              <div
                                className={`h-8 w-8 ${color.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
                              >
                                <Mail className={`h-4 w-4 ${color.text}`} />
                              </div>
                              <span className='block truncate font-medium text-slate-900'>
                                {link.email}
                              </span>
                            </div>
                          </td>
                          <td
                            className='whitespace-nowrap px-4 py-3 text-slate-600'
                            style={{ fontSize: '0.8rem' }}
                          >
                            {link.patient ? (
                              link.patient.full_name
                            ) : (
                              <span className='text-slate-400'>New Patient</span>
                            )}
                          </td>
                          {!isGuided && (
                            <td
                              className='whitespace-nowrap px-4 py-3 text-slate-600'
                              style={{ fontSize: '0.8rem' }}
                            >
                              {link.practitioner?.full_name || '--'}
                            </td>
                          )}
                          <td
                            className='whitespace-nowrap px-4 py-3 text-slate-600'
                            style={{ fontSize: '0.8rem' }}
                          >
                            <div className='flex items-center gap-1'>
                              <Calendar className='h-3.5 w-3.5 text-slate-400' />
                              {formatToLocalDate(link.created_at, 'MMM DD, YYYY', true)}
                            </div>
                          </td>
                          <td
                            className='whitespace-nowrap px-4 py-3 text-slate-600'
                            style={{ fontSize: '0.8rem' }}
                          >
                            <div className='flex items-center gap-1'>
                              <Clock className='h-3.5 w-3.5 text-slate-400' />
                              {formatToLocalDate(link.expires_at, 'MMM DD, YYYY', true)}
                            </div>
                          </td>
                          <td className='whitespace-nowrap px-4 py-3'>
                            {getFormLinkStatusBadge(link.status, link.expires_at)}
                          </td>
                          <td
                            className='sticky right-0 px-2 py-3'
                            style={{
                              background:
                                'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                            }}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8 hover:bg-slate-100'
                                >
                                  <MoreHorizontal className='h-4 w-4 text-slate-500' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end' className='w-48'>
                                <DropdownMenuItem
                                  onClick={() => handleCopyLink(link.token)}
                                  className='cursor-pointer text-sm'
                                >
                                  <Copy className='mr-2 h-4 w-4 text-slate-500' />
                                  Copy Link
                                </DropdownMenuItem>
                                {canResend && hasEditPermission && (
                                  <DropdownMenuItem
                                    onClick={() => handleResendLink(link)}
                                    className='cursor-pointer text-sm'
                                  >
                                    <RefreshCw className='mr-2 h-4 w-4 text-slate-500' />
                                    Resend Email
                                  </DropdownMenuItem>
                                )}
                                {canRevoke && hasEditPermission && (
                                  <DropdownMenuItem
                                    onClick={() => handleRevokeLink(link)}
                                    className='cursor-pointer text-sm text-red-600'
                                  >
                                    <Ban className='mr-2 h-4 w-4' />
                                    Revoke Link
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {formLinksPagination.totalPages > 1 && (
              <div className='flex items-center justify-between border-t border-slate-200 px-4 py-3'>
                <SmartPagination
                  currentPage={formLinksPagination.page}
                  totalPages={formLinksPagination.totalPages}
                  onPageChange={page => setFormLinksPagination(prev => ({ ...prev, page }))}
                  maxVisiblePages={3}
                />
                <p className='text-xs text-slate-500'>
                  Total:{' '}
                  <span className='font-medium text-slate-700'>
                    {formLinksPagination.totalItems}
                  </span>{' '}
                  Links
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Patient Form Drawer */}
      <PatientFormDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        patient={editingPatient}
        onSubmit={handlePatientSubmit}
        assignedPractitioner={isPractitioner ? user?.practitionerId : ''}
        assignedClinic={clinic?.id}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title='Delete Patient'
        description='Are you sure you want to delete this patient? This action cannot be undone.'
        itemName={
          patientToDelete ? `${patientToDelete.first_name} ${patientToDelete.last_name}` : undefined
        }
        itemType='patient'
        onConfirm={handleDeleteConfirm}
      />

      {/* Send Form Link Dialog */}
      <ConfirmDeleteDialog
        open={sendFormLinkDialogOpen}
        onOpenChange={setSendFormLinkDialogOpen}
        title='Send Patient Form Link'
        description={`Send a secure form link to ${getDefaultContactValue(patientForFormLink?.contacts, 'email') || 'this patient'} to update their information?`}
        itemName={
          patientForFormLink
            ? `${patientForFormLink.first_name} ${patientForFormLink.last_name}`
            : undefined
        }
        itemType='form link'
        onConfirm={handleSendFormLinkConfirm}
      />

      {/* Revoke Form Link Dialog */}
      <ConfirmDeleteDialog
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        title='Revoke Form Link'
        description={`Are you sure you want to revoke this form link? The patient will no longer be able to use it.`}
        itemName={linkToRevoke ? linkToRevoke.email : undefined}
        itemType='Form link'
        onConfirm={handleRevokeConfirm}
        actionType='revoke'
        successMessage='Form link revoked successfully'
      />

      {/* Send New Link Dialog */}
      <SendPatientFormLinkDialog
        open={sendNewLinkDialogOpen}
        onOpenChange={setSendNewLinkDialogOpen}
        clinicId={clinic?.id || ''}
        practitionerId={user?.practitionerId || ''}
        isPractitioner={isPractitioner}
        practitioners={practitioners}
        isGuided={isGuided}
        onSuccess={() => {
          if (activeTab === 'form-links') {
            fetchFormLinks();
          }
        }}
      />

      {/* Filter Drawer */}
      <Sheet open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
        <SheetContent
          className='flex w-full max-w-[400px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'
          style={{ minWidth: 'auto' }}
        >
          {/* Header */}
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <SheetTitle className='font-semibold text-slate-900' style={{ fontSize: '1.05rem' }}>
              Filter & Sort
            </SheetTitle>
            <SheetClose asChild>
              <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
                <X className='h-5 w-5 text-slate-600' />
              </button>
            </SheetClose>
          </div>

          {/* Content */}
          <div className='flex-1 space-y-4 overflow-y-auto px-6 py-6'>
            {/* Gender Filter */}
            <div className='space-y-1'>
              <Label className='font-medium' style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Gender
              </Label>
              <Select value={tempGender} onValueChange={setTempGender}>
                <SelectTrigger className='h-10 w-full border-slate-200 transition-colors duration-200 focus:border-black focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'>
                  <SelectValue placeholder='Select gender' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Genders</SelectItem>
                  {GENDER_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className='space-y-1'>
              <Label className='font-medium' style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Status
              </Label>
              <Select value={tempStatus} onValueChange={setTempStatus}>
                <SelectTrigger className='h-10 w-full border-slate-200 transition-colors duration-200 focus:border-black focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'>
                  <SelectValue placeholder='Select status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Practitioner Filter */}
            {!isGuided && (
              <div className='space-y-1'>
                <Label className='font-medium' style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                  Practitioner
                </Label>
                <Select value={tempPractitioner} onValueChange={setTempPractitioner}>
                  <SelectTrigger className='h-10 w-full border-slate-200 transition-colors duration-200 focus:border-black focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'>
                    <SelectValue placeholder='Select practitioner' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Practitioners</SelectItem>
                    {practitioners.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date of Birth Range */}
            <div className='space-y-1'>
              <Label className='font-medium' style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Date of Birth Range
              </Label>
              <div className='flex gap-2'>
                <DatePicker
                  value={tempDobFrom}
                  onValueChange={setTempDobFrom}
                  placeholder='From date'
                  maxDate={tempDobTo || new Date()}
                  className='h-10 flex-1 border-slate-200'
                />
                <DatePicker
                  value={tempDobTo}
                  onValueChange={setTempDobTo}
                  placeholder='To date'
                  minDate={tempDobFrom}
                  maxDate={new Date()}
                  className='h-10 flex-1 border-slate-200'
                />
              </div>
            </div>

            {/* Sort By */}
            <div className='space-y-1'>
              <Label className='font-medium' style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Sort By
              </Label>
              <Select value={tempSortBy} onValueChange={setTempSortBy}>
                <SelectTrigger className='h-10 w-full border-slate-200 transition-colors duration-200 focus:border-black focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'>
                  <SelectValue placeholder='Select field' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='created_at'>Date Created</SelectItem>
                  <SelectItem value='name'>Patient Name</SelectItem>
                  <SelectItem value='status'>Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort Order */}
            <div className='space-y-1'>
              <Label className='font-medium' style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Sort Order
              </Label>
              <Select
                value={tempSortOrder}
                onValueChange={value => setTempSortOrder(value as 'ASC' | 'DESC')}
              >
                <SelectTrigger className='h-10 w-full border-slate-200 transition-colors duration-200 focus:border-black focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'>
                  <SelectValue placeholder='Select order' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='ASC'>Ascending</SelectItem>
                  <SelectItem value='DESC'>Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer */}
          <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
            <Button
              variant='ghost'
              className='font-medium text-slate-700 hover:bg-transparent hover:text-slate-900'
              onClick={() => {
                setTempGender('all');
                setTempStatus('all');
                setTempPractitioner('all');
                setTempSortBy('created_at');
                setTempSortOrder('DESC');
                setTempDobFrom(undefined);
                setTempDobTo(undefined);
              }}
            >
              Reset all
            </Button>
            <Button
              className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
              onClick={() => {
                setGenderFilter(tempGender);
                setStatusFilter(tempStatus);
                setPractitionerFilter(tempPractitioner);
                setDobFromFilter(tempDobFrom);
                setDobToFilter(tempDobTo);
                setPagination(prev => ({
                  ...prev,
                  page: 1,
                  sortBy: tempSortBy,
                  sortOrder: tempSortOrder,
                }));
                setIsFilterDrawerOpen(false);
              }}
            >
              Apply Filter
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Status Update Dialog */}
      {statusDialogOpen && patientForStatusUpdate && (
        <UpdatePatientStatusDialog
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
          patient={patientForStatusUpdate}
          newStatus={pendingStatus}
          onSuccess={handleStatusUpdateSuccess}
        />
      )}
    </div>
  );
};

export default ClinicPatients;
